import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL MOMENTUM - Contacts API
// =============================================================================

export const royalMomentumContactsApi = createApi({
    reducerPath: "royalMomentumContactsApi",
    baseQuery: apiRequestBaseQuery,
    tagTypes: ["Contacts", "FavoriteContacts"],
    endpoints: (builder) => ({
        // Get Contacts — V2.
        // `agency_id` (= selectedBrand.uid = agency_id_no_r) replaces `travel_agency_uid`.
        // contact_type "internal" = agency-internal contacts (per GET_CONTACTS_V2 spec).
        getContacts: builder.query({
            query: ({ page = 1, limit = 10, search = "", travelAgencyUid = null } = {}) => {
                const userDetails = getUserDetails();
                const data = {
                    user_uid: userDetails?.user_details?.uid,
                    agency_id: travelAgencyUid,
                    contact_type: "internal",
                    page,
                    limit,
                };
                if (search) data.search = search;
                return { requestType: requestType.getContactsV2, data };
            },
            providesTags: ["Contacts"],
        }),
        // Delete Contact — V2. V1 was keyed by `contact_uid` alone; V2 also takes `agency_id`
        // (= selectedBrand.uid = agency_id_no_r), matching the other V2 writes in this slice.
        //
        // `allow_delete_import_data` guards Salesforce-imported records. The FE now always sends
        // true, so a delete removes the contact in Salesforce as well as in Royal Momentum; the
        // delete popup shows the Salesforce warning copy to match. false (the BE default) would
        // limit the delete to Royal Momentum. See RoyalMomentumContacts.ALLOW_DELETE_IMPORT_DATA.
        deleteContact: builder.mutation({
            query: ({ contactUid, travelAgencyUid = null, allowDeleteImportData = true }) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.deleteContactV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: travelAgencyUid,
                        contact_uid: contactUid,
                        allow_delete_import_data: allowDeleteImportData,
                    },
                };
            },
            invalidatesTags: ["Contacts", "FavoriteContacts"],
        }),
        // Get Favorite Contacts — V2 (`agency_id` replaces `travel_agency_uid`).
        getFavoriteContacts: builder.query({
            query: ({ travelAgencyUid = null } = {}) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getFavoriteContactsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: travelAgencyUid,
                        page: 1,
                        limit: 3,
                    },
                };
            },
            providesTags: ["FavoriteContacts"],
        }),
        // Add Favorite Contact — V2 (`agency_id` replaces `travel_agency_uid`).
        addFavoriteContact: builder.mutation({
            query: ({ contactUid, travelAgencyUid = null }) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.addFavoriteContactV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: travelAgencyUid,
                        contact_uid: contactUid,
                    },
                };
            },
            invalidatesTags: ["FavoriteContacts"],
        }),
        // Remove Favorite Contact — V2 (`agency_id` replaces `travel_agency_uid`).
        removeFavoriteContact: builder.mutation({
            query: ({ contactUid, travelAgencyUid = null }) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.removeFavoriteContactV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: travelAgencyUid,
                        contact_uid: contactUid,
                    },
                };
            },
            invalidatesTags: ["FavoriteContacts"],
        }),
        // Add Contact — V2 (`agency_id` replaces `travel_agency_uid`).
        addContact: builder.mutation({
            query: (contactData) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.addContactV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: contactData.travelAgencyUid || null,
                        first_name: contactData.firstName,
                        last_name: contactData.lastName,
                        email: contactData.email,
                        phone: contactData.phone,
                        mobile_phone: contactData.phone,
                        title: contactData.jobTitle,
                        mailing_country: contactData.region,
                    },
                };
            },
            invalidatesTags: ["Contacts"],
        }),
        // Update Contact — V2 (`agency_id` replaces `travel_agency_uid`).
        updateContact: builder.mutation({
            query: (contactData) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.updateContactV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: contactData.travelAgencyUid || null,
                        contact_uid: contactData.id,
                        first_name: contactData.firstName,
                        last_name: contactData.lastName,
                        email: contactData.email,
                        phone: contactData.phone,
                        mobile_phone: contactData.phone,
                        title: contactData.jobTitle,
                        mailing_country: contactData.region,
                    },
                };
            },
            invalidatesTags: ["Contacts"],
        }),
    }),
});

export const {
    useGetContactsQuery,
    useGetFavoriteContactsQuery,
    useAddContactMutation,
    useUpdateContactMutation,
    useDeleteContactMutation,
    useAddFavoriteContactMutation,
    useRemoveFavoriteContactMutation,
} = royalMomentumContactsApi;
